import { useEffect, useState } from 'react';

import LabellessOption from 'components/account/Returns/ReturnOptions/LabellessOption';
import MailLabelOption from 'components/account/Returns/ReturnOptions/MailLabelOption';
import PickupOption from 'components/account/Returns/ReturnOptions/PickupOption';
import PrintLabelOption from 'components/account/Returns/ReturnOptions/PrintLabelOption';
import WFMPackagelessOption from 'components/account/Returns/ReturnOptions/WFMPackagelessOption';
import { SmallLoader } from 'components/Loader';
import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import { track } from 'apis/amethyst';
import { evReturnTransportationOptions } from 'events/returns';
import UPSPackagelessOption from 'components/account/Returns/ReturnOptions/UPSPackagelessOption';
import UPSIcon from 'images/zappos/ups-logo.svg';
import { BACKUP_LABEL_METHODS, UPS_RETURN_METHODS } from 'constants/returnLabelOptions';
import { cn } from 'helpers/classnames';

import css from 'styles/containers/returns/shippingMethodChooserWithInstructions.scss';

const SHIPPING_METHOD_OPTIONS = {
  [PROVIDE_LABEL_OPTIONS.PRINT]: PrintLabelOption,
  [PROVIDE_LABEL_OPTIONS.PICKUP]: PickupOption,
  [PROVIDE_LABEL_OPTIONS.LABELLESS]: LabellessOption,
  [PROVIDE_LABEL_OPTIONS.MAIL]: MailLabelOption,
  [PROVIDE_LABEL_OPTIONS.UPS_PACKAGELESS]: UPSPackagelessOption,
  [PROVIDE_LABEL_OPTIONS.WFM_PACKAGELESS]: WFMPackagelessOption
};

const getCategorizedReturnMethods = (finalOptions, categorizedOptionNames, optionProps, title, icon) => {
  const options = finalOptions
    .filter(option => categorizedOptionNames.includes(option))
    .map(option => {
      const Option = SHIPPING_METHOD_OPTIONS[option];
      return !!Option && <Option {...optionProps} key={option} />;
    });
  return options.length > 0 ? (
    <div className={css.categorizedReturnMethods}>
      <h2 className={cn(css.categorizedReturnMethodTitle, 'zappos:body-l-medium')}>
        <span>
          <img className={css.categorizeIcon} src={icon} alt={'category icon'} />
        </span>
        {title}
      </h2>
      {options}
    </div>
  ) : null;
};

const hasPackagelessMethods = finalOptions => finalOptions.includes(PROVIDE_LABEL_OPTIONS.UPS_PACKAGELESS);

const showPackagelessMessage = (hasMultiItems, finalOptions) => hasMultiItems && hasPackagelessMethods(finalOptions);

const ShippingMethodChooserWithInstructions = ({
  hasMultiItems,
  shipping,
  labelDelivery,
  UPSPickUpAddress,
  showMailedLabelOption = false,
  showLabellessReturns = false,
  showPackagelessReturn = false,
  options,
  isLoading,
  showAvailableReturnLabels,
  arePickupReturnsAuthorized = false,
  onLabelDeliverySelect,
  onUpdateShippingPhone,
  onCancelReturn,
  onUpdateMailingAddressOrName,
  onSubmitReturn,
  customerInfo,
  isStoreCreditEnabled,
  labelPaymentType,
  zeroOriginalPaymentBalance,
  afterPayOrder
}) => {
  const [showInstructions, setShowInstructions] = useState(null);
  const showInstructionsHandler = event => setShowInstructions(event.target.value !== showInstructions ? event.target.value : null);

  const onReturnMethodSelect = event => {
    onLabelDeliverySelect(event);
    if (event.target.value !== showInstructions) {
      setShowInstructions(null);
    }
  };

  const optionProps = {
    shipping,
    labelDelivery,
    UPSPickUpAddress,
    showMailedLabelOption,
    showLabellessReturns,
    showPackagelessReturn,
    onLabelDeliverySelect,
    onUpdateShippingPhone,
    onCancelReturn,
    onUpdateMailingAddressOrName,
    onSubmitReturn,
    customerInfo,
    onReturnMethodSelect,
    showInstructions,
    showInstructionsHandler,
    isStoreCreditEnabled,
    labelPaymentType,
    zeroOriginalPaymentBalance,
    afterPayOrder
  };
  // handles the transportOption fetch killswitch
  const availableOptions = showAvailableReturnLabels ? options : BACKUP_LABEL_METHODS;
  // handles the UPS_PICKUP activation killswitch
  let finalOptions = arePickupReturnsAuthorized ? availableOptions : availableOptions.filter(option => option !== PROVIDE_LABEL_OPTIONS.PICKUP);
  // TODO : REMOVE WHEN LFBF AVAILABLE FOR EXCHANGES
  finalOptions = finalOptions.includes(PROVIDE_LABEL_OPTIONS.UPS_PACKAGELESS)
    ? finalOptions.filter(option => option !== PROVIDE_LABEL_OPTIONS.LABELLESS)
    : finalOptions;
  useEffect(() => {
    if (!isLoading) {
      track(() => [
        evReturnTransportationOptions,
        { availableReturnLabels: availableOptions, displayedReturnLabels: finalOptions, arePickupReturnsAuthorized: arePickupReturnsAuthorized }
      ]);
    }
  }, [availableOptions, finalOptions, arePickupReturnsAuthorized, isLoading]);

  return (
    <div className={css.container}>
      {isLoading ? (
        <SmallLoader />
      ) : (
        <>
          {showPackagelessMessage(hasMultiItems, finalOptions) && (
            <div className={css.returnMutipleItemsNewMethodMessage}>
              <h3 className="zappos:body-s">Drop-off Without Shipping Box and Label</h3>
              <p className="zappos:body-s">You can now return items without the need to use shipping boxes and print shipping labels.</p>
            </div>
          )}
          {
            // UPS methods
            getCategorizedReturnMethods(finalOptions, UPS_RETURN_METHODS, optionProps, 'UPS', UPSIcon)
          }
        </>
      )}
    </div>
  );
};

export default ShippingMethodChooserWithInstructions;
